// BlockData > BlockData
import { IBlockType } from '../BlockType'
import { BlockGroupState } from '../BlockGroup'
import { hasIntersection, hasDifference } from '../utils/Array'
import { IBlockData, BlockDataOptionalProps } from './index'
import { BlockDataStatus } from './IBlockData'
import { randomInteger } from '../utils/Number'
import { RGBAArrayToCssString, colorNameToRGBA, blend } from '../utils/Color'

export default class BlockData<BlockTypesType extends object> implements IBlockData<BlockTypesType> {
	private _indexes: number[]
	private _types: IBlockType<BlockTypesType>[]
	private _label: string = ''
	private _id: number
	private _initBlockGroupState: Partial<BlockGroupState>
	private _originalIndexes: number[]
	private _status?: BlockDataStatus

	static areEqual<BlockTypesType>(a: IBlockData<BlockTypesType>, b: IBlockData<BlockTypesType>): boolean {
		if (!a || !b) {
			console.warn('Block(s) are undefined', a, b)
			return false
		}

		if (!hasDifference(a.indexes, b.indexes) && !hasDifference(a.types, b.types) && a.status === b.status) {
			return true
		}

		return false
	}

	static arraysAreEqual<BlockTypesType>(a: IBlockData<BlockTypesType>[], b: IBlockData<BlockTypesType>[]) {
		if (!a || !b) {
			console.warn('Block array(s) are undefined', a, b)
			return false
		}

		let result = a.length === b.length

		for (let i = 0, n = a.length; i < n; i++) {
			if (!result) break

			const blockA = a[i]

			for (let j = 0, m = b.length; j < m; j++) {
				const blockB = b[j]

				if (!BlockData.areEqual(blockA, blockB)) {
					result = false
					break
				}
			}
		}

		return result
	}

	constructor(
		indexes: number[],
		types: IBlockType<BlockTypesType>[],
		{ initBlockGroupState, id, status }: BlockDataOptionalProps = {}
	) {
		if (!indexes) {
			throw new Error('Block indexes are required. If none, provide an empty array.')
		}

		if (!types) {
			throw new Error('Block types are required. If none, provide an empty array.')
		}

		this._id = id || -randomInteger()
		this._indexes = indexes
		this._types = types
		this._originalIndexes = indexes
		this._status = status || !id ? BlockDataStatus.POST : undefined
		this._initBlockGroupState = initBlockGroupState || {}
	}

	get id(): number {
		return this._id
	}

	get indexes(): number[] {
		return this._indexes
	}

	set indexes(newIndexes: number[]) {
		this._indexes = newIndexes

		// update the status accordingly
		if (this.id > 0) {
			if (newIndexes.length === 0) this._status = BlockDataStatus.DELETE
			else if (
				!hasIntersection(newIndexes, this._originalIndexes) ||
				hasDifference(newIndexes, this._originalIndexes)
			) {
				this._status = BlockDataStatus.PUT
			} else this._status = undefined
		} else if (newIndexes.length > 0) {
			this._status = BlockDataStatus.POST
		} else {
			this._status = undefined
		}
	}

	get types(): IBlockType<BlockTypesType>[] {
		return this._types
	}

	set types(value: IBlockType<BlockTypesType>[]) {
		this._types = value
	}

	get label(): string {
		return this._label
	}

	set label(value: string) {
		this._label = value
	}

	get color(): string[] {
		return this.types.map(type => type.color)
	}

	get cssColor(): string {
		return RGBAArrayToCssString(blend(...this.color.map((c: string) => colorNameToRGBA(c))))
	}

	set originalIndexes(newIndexes: number[]) {
		this._originalIndexes = newIndexes
	}

	get status(): BlockDataStatus | undefined {
		return this._status
	}

	set status(value: BlockDataStatus | undefined) {
		this._status = value
	}

	get initBlockGroupState(): Partial<BlockGroupState> {
		return this._initBlockGroupState
	}

	intersectsWith = (indexes: number[]): boolean => {
		return hasIntersection(indexes, this.indexes)
	}

	addBlockType(blockType: IBlockType<BlockTypesType>) {
		this.types.push(blockType)

		return this.types
	}

	removeBlockType(blockType: IBlockType<BlockTypesType>) {
		const index = this.types.indexOf(blockType)

		if (index > -1) {
			this.types.splice(index, 1)
		}

		return this.types
	}
}
